var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.
import { createClientCredentialsAuthorizationJwt } from './AccessKey.js';
import { getOauthTokenUrl } from '../utils/DomainUtils.js';
import { StringUtils } from '@laserfiche/lf-js-utils';
import { ProblemDetails } from '../ProblemDetails.js';
import { ApiException } from '../ApiException.js';
const CONTENT_TYPE_WWW_FORM_URLENCODED = 'application/x-www-form-urlencoded';
/**
 * An object to interact with the Laserfiche Cloud OAuth 2.0 token endpoint.
 */
export class TokenClient {
    /**
     * Constructor for a TokenClient used to interact with the Laserfiche Cloud OAuth 2.0 token endpoint.
     * @param regionalDomain - regional specific host, such as 'laserfiche.com', or 'eu.laserfiche.com'
     */
    constructor(regionalDomain) {
        this._accessTokenSPErrMsg = "Get access token error.";
        this._refreshTokenErrMsg = "Refresh access token error.";
        this._acessTokenCodeErrMsg = "Get access token from code error.";
        this._baseUrl = getOauthTokenUrl(regionalDomain);
    }
    /**
     * Gets a refreshed access token given a refresh token
     * @param refresh_token - Refresh token
     * @param client_id - OAuth application client id
     * @param client_secret - OPTIONAL OAuth application client secret. Required for web apps.
     */
    refreshAccessToken(refresh_token, client_id, client_secret) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const request = this.createRefreshTokenRequest(refresh_token, client_id, client_secret);
            let url = this._baseUrl;
            const res = yield fetch(url, request);
            if (res.status === 200) {
                const getAccessTokenResponse = yield res.json();
                return getAccessTokenResponse;
            }
            else if (((_a = res.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('json')) === true) {
                const errorResponse = yield res.json();
                const problemDetails = ProblemDetails.fromJS(errorResponse);
                const apiException = new ApiException((_b = problemDetails.title) !== null && _b !== void 0 ? _b : this._refreshTokenErrMsg, problemDetails.status, res.headers, problemDetails);
                throw apiException;
            }
            else {
                throw new ApiException(this._refreshTokenErrMsg, res.status, res.headers, null);
            }
        });
    }
    /**
     * Gets an OAuth access token given an OAuth code
     * @param code - Authorization code
     * @param redirect_uri - Authorization endpoint redirect uri
     * @param client_id - OAuth application client id
     * @param client_secret - OPTIONAL OAuth application client secret. Required for web apps.
     * @param code_verifier - OPTIONAL PKCE code verifier. Required for SPA apps.
     * @param scope - OPTIONAL The requested space-delimited scopes for the access token.
     */
    getAccessTokenFromCode(code, redirect_uri, client_id, client_secret, code_verifier, scope) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const request = this.createAuthorizationCodeTokenRequest(code, redirect_uri, client_id, code_verifier, client_secret, scope);
            let url = this._baseUrl;
            const res = yield fetch(url, request);
            if (res.status === 200) {
                const getAccessTokenResponse = yield res.json();
                return getAccessTokenResponse;
            }
            else if (((_a = res.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('json')) === true) {
                const errorResponse = yield res.json();
                const problemDetails = ProblemDetails.fromJS(errorResponse);
                const apiException = new ApiException((_b = problemDetails.title) !== null && _b !== void 0 ? _b : this._acessTokenCodeErrMsg, problemDetails.status, res.headers, problemDetails);
                throw apiException;
            }
            else {
                throw new ApiException(this._acessTokenCodeErrMsg, res.status, res.headers, null);
            }
        });
    }
    /**
     * Gets an OAuth access token given a Laserfiche cloud service principal key and an OAuth service application access key.
     * @param servicePrincipalKey - Laserfiche cloud service principal key
     * @param accessKey - OAuth service application access key
     * @param scope - OPTIONAL The requested space-delimited scopes for the access token.
     */
    getAccessTokenFromServicePrincipal(servicePrincipalKey, accessKey, scope) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const token = createClientCredentialsAuthorizationJwt(servicePrincipalKey, accessKey);
            let body = `grant_type=client_credentials`;
            if (scope) {
                body += `&scope=${encodeURIComponent(scope)}`;
            }
            const req = {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/x-www-form-urlencoded',
                    withCredentials: 'true',
                    credentials: 'include',
                    Authorization: `Bearer ${token}`,
                }),
                body: body
            };
            const url = this._baseUrl;
            const res = yield fetch(url, req);
            if (res.status === 200) {
                const getAccessTokenResponse = yield res.json();
                return getAccessTokenResponse;
            }
            else if (((_a = res.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('json')) === true) {
                const errorResponse = yield res.json();
                const problemDetails = ProblemDetails.fromJS(errorResponse);
                const apiException = new ApiException((_b = problemDetails.title) !== null && _b !== void 0 ? _b : this._accessTokenSPErrMsg, problemDetails.status, res.headers, problemDetails);
                throw apiException;
            }
            else {
                throw new ApiException(this._accessTokenSPErrMsg, res.status, res.headers, null);
            }
        });
    }
    createAuthorizationCodeTokenRequest(code, redirect_uri, client_id, code_verifier, client_secret, scope) {
        const request = { method: 'POST' };
        const headers = this.getPostRequestHeaders(client_id, client_secret);
        const body = {
            grant_type: 'authorization_code',
            code,
            redirect_uri,
            client_id,
        };
        if (code_verifier) {
            body['code_verifier'] = code_verifier;
        }
        if (scope) {
            body['scope'] = scope;
        }
        const requestBody = this.objToWWWFormUrlEncodedBody(body);
        request.headers = headers;
        request.body = requestBody;
        return request;
    }
    createRefreshTokenRequest(refreshToken, client_id, client_secret) {
        const request = { method: 'POST' };
        const headers = this.getPostRequestHeaders(client_id, client_secret);
        let body = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id
        };
        const requestBody = this.objToWWWFormUrlEncodedBody(body);
        request.headers = headers;
        request.body = requestBody;
        return request;
    }
    getPostRequestHeaders(client_id, client_secret) {
        const headers = {
            'Content-Type': CONTENT_TYPE_WWW_FORM_URLENCODED,
        };
        if (client_secret) {
            const basicCredentials = client_id + ':' + client_secret;
            const encodedClientSecret = StringUtils.stringToBase64(basicCredentials);
            headers['Authorization'] = 'Basic ' + encodedClientSecret;
        }
        return headers;
    }
    objToWWWFormUrlEncodedBody(obj) {
        const urlSearchParams = new URLSearchParams();
        for (const i in obj) {
            urlSearchParams.set(i, obj[i]);
        }
        return urlSearchParams.toString();
    }
}
